import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Col,
  List,
  Row,
  Button,
  Divider,
  Empty,
  Tooltip,
} from "antd";
import { InfoCircleOutlined, StarFilled } from "@ant-design/icons";
import contact from "@assets/icons/contact.svg";
import locationMarker from "@assets/icons/location-marker.svg";
import { useCart } from "contexts/CartContext";
import { errorLog } from "logging";
import { ProductApi } from "services/ProductApi";
import { useNavigate } from "react-router-dom";
import CartItem from "@components/CartItem/CartItem";
import ProductList from "@containers/ProductList/ProductList";
import Helpers from "@utils/Helpers";
import "./Cart.scss";
import { useUnit } from "contexts/UnitContext";
import { useUser } from "contexts/UserContext";

const Cart: React.FC = () => {
  const { userCart, productSelected, setProductSelected } = useCart();
  const { setIsOpenChatbox, setSelectedReceiver, selectedReceiver } = useUser();
  const [carts, setCarts] = useState<any[]>([]);
  const [subTotal, setSubTotal] = useState<string>("0");
  const [allProducts, setAllProducts] = useState<any>(null);
  const navigate = useNavigate();
  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const { units, convertCurrency } = useUnit();

  // FETCH ALL PRODUCTS
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await ProductApi.GetAll();
        if (response.status === 200) {
          setAllProducts(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch data. Please try again");
        errorLog("ProductCatalog.tsx - fetchProducts", error);
      }
    };

    if (!allProducts) {
      fetchProducts();
    }
  }, [allProducts]);

  // REFACTOR CART
  useEffect(() => {
    if (userCart.length > 0) {
      const groupedData: any = Object.values(
        userCart.reduce((acc: any, item: any) => {
          const displayName = item.farm_details.display_name;
          if (!acc[displayName]) {
            acc[displayName] = [];
          }
          acc[displayName].push(item);
          return acc;
        }, {})
      );
      setCarts(groupedData);
      setIsAgreed(false);
    } else {
      setCarts([]);
      setIsAgreed(false);
    }
  }, [userCart]);

  // CALCULATE SUBTOTAL
  useEffect(() => {
    let subTotalPrice: number = 0;
    productSelected.forEach((item: any) => {
      if (item.isSelected) {
        const productItem = userCart.find(
          (ite: any) => ite.product_id === item.productId
        );
        const unitPrice = convertCurrency(
          productItem.discounted_price
            ? productItem.discounted_price
            : productItem.unit == "Ton"
            ? productItem.price_per_tons
            : productItem.price_per_bale,
          "USD",
          units.currency
        );
        let pricePerItem = 0;
        if (productItem.unit == "Ton") {
          pricePerItem =
            productItem?.quantity *
            (productItem.bale_weight / 1000) *
            unitPrice;
        } else {
          pricePerItem = productItem?.quantity * unitPrice;
        }
        subTotalPrice += pricePerItem;
      }
    });

    setSubTotal(Helpers.formatNumber(subTotalPrice, 2));
  }, [productSelected, userCart]);

  // HANDLE CHECK ALL
  const handleCheckSelected = (isChecked: boolean, cartsIndex: number) => {
    let updatedItems: any[] = [...productSelected];
    carts[cartsIndex].forEach((cart: any) => {
      updatedItems = updatedItems.map((item: any) => {
        if (item.productId === cart.product_id) {
          return { ...item, isSelected: isChecked ? true : false };
        } else {
          return item;
        }
      });
    });
    setProductSelected(updatedItems);
  };

  const handleCheckOut = () => {
    const selectedProducts = productSelected
      .filter((product) => product.isSelected)
      .map((product) => product.productId);

    const selectedShops: any[] = [];

    carts.forEach((shop) => {
      shop.forEach((product: any) => {
        if (selectedProducts.includes(product.product_id)) {
          if (!selectedShops.includes(product.farm_details.producer_id)) {
            selectedShops.push(product.farm_details.producer_id);
          }
        }
      });
    });
    if (selectedShops.length > 1) {
      Helpers.notification.warning("Please select only one producer");
      return;
    }

    if (selectedShops.length === 0) {
      Helpers.notification.warning("Please select at least one product");
      return;
    }

    const selectedProductsQuantity: any = [];
    carts.forEach((shop) => {
      shop.forEach((product: any) => {
        if (selectedProducts.includes(product.product_id)) {
          selectedProductsQuantity.push(product.quantity);
        }
      });
    });

    if (selectedProductsQuantity.includes(0)) {
      Helpers.notification.warning("Please select quantity for products");
      return;
    }

    let indexSelectedShop: any = -1;
    carts.find((cart, index) => {
      if (cart[0].farm_details.producer_id === selectedShops[0]) {
        indexSelectedShop = index;
        return index;
      }
    });
    if (!isAgreed) {
      Helpers.notification.warning("Please agree with producer's policy");
      return;
    } else {
      navigate("/checkout");
    }
  };

  return (
    <div className="Cart">
      <div className="container">
        <div className="page-title">Cart</div>
        <Row gutter={14}>
          <Col span={16}>
            {carts.length === 0 && (
              <div className="empty-section">
                <Empty />
              </div>
            )}
            {carts.map((item: any, index: number) => {
              return (
                <List
                  className="cart-list"
                  key={index}
                  header={
                    <div className="cart-header">
                      <div className="header-item">
                        <Checkbox
                          onChange={(e) =>
                            handleCheckSelected(e.target.checked, index)
                          }
                          checked={(() => {
                            let isCheckedAll = true;
                            item.forEach((cartItem: any) => {
                              productSelected.forEach((itemSelected: any) => {
                                if (
                                  cartItem.product_id === itemSelected.productId
                                ) {
                                  if (!itemSelected.isSelected) {
                                    isCheckedAll = false;
                                  }
                                }
                              });
                            });
                            return isCheckedAll;
                          })()}
                        />
                        <div className="farm-info">
                          <div className="name">
                            <b
                              onClick={() => {
                                window.open(
                                  `/producer/detail?producerId=${item[0].farm_details.producer_id}`
                                );
                              }}
                            >
                              {item[0].farm_details.display_name}
                            </b>
                            <StarFilled
                              style={{ color: "var(--secondary-500)" }}
                            />{" "}
                            4.0
                          </div>
                          <div className="location">
                            <img
                              src={locationMarker}
                              alt="contact"
                              width={20}
                            />
                            {item[0].farm_details.farm_province}
                          </div>
                        </div>
                        <Button
                          className="contact-btn"
                          onClick={() => {
                            setIsOpenChatbox(true);
                            setSelectedReceiver({
                              ...selectedReceiver,
                              receiverId: item[0]?.farm_details?.producer_id,
                              receiverName: item[0]?.farm_details?.display_name,
                              receiverAvatar:
                                item[0]?.farm_details?.farm_avatar,
                            });
                          }}
                        >
                          <img
                            src={contact}
                            alt="contact"
                            width={20}
                            className="contact-icon"
                          />
                          Contact Producer
                        </Button>
                      </div>
                      <div className="policy">
                        <Tooltip
                          placement="right"
                          title={
                            "Buyers can get a full refund if they cancel before shipping"
                          }
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      </div>
                    </div>
                  }
                  bordered
                  dataSource={item}
                  renderItem={(ite) => {
                    return (
                      <List.Item>
                        <CartItem
                          data={ite}
                          productSelected={productSelected}
                          setProductSelected={setProductSelected}
                          type={"cart"}
                        />
                      </List.Item>
                    );
                  }}
                />
              );
            })}
          </Col>
          <Col span={8}>
            <div className="summary">
              <div className="summary-box">
                <div className="summary-title">Summary</div>
                <div className="summary-item">
                  {
                    productSelected.filter((item: any) => item.isSelected)
                      .length
                  }{" "}
                  items:
                  <div className="price">${subTotal}</div>
                </div>
              </div>
              <Divider className="divider" />
              <div className="subtotal-box">
                <div className="subtotal-content">
                  Subtotal
                  <div className="subtotal-price">${subTotal}</div>
                </div>
                <Checkbox
                  checked={isAgreed}
                  className="mt-4"
                  onChange={(e) => setIsAgreed(e.target.checked)}
                >
                  Agreed with Producer's policy
                </Checkbox>
                <Button
                  className="checkout-btn mt-2"
                  onClick={() => handleCheckOut()}
                  disabled={carts.length === 0}
                >
                  Proceed to Checkout
                </Button>
              </div>
            </div>
          </Col>
        </Row>
        <ProductList type="Other Products" products={allProducts} />
      </div>
    </div>
  );
};

export default Cart;
